import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import styles from "./Profile.module.css";
import { updateUserProfile } from "../../../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

const BASE_URL = "https://admin.osleague.online/api";

export default function Profile() {
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [editName, setEditName] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editConfirm, setEditConfirm] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [profileRes, historyRes, rankRes] = await Promise.all([
          axios.get(`${BASE_URL}/profile`, { headers }),
          axios.get(`${BASE_URL}/predictions/history`, { headers }),
          axios.get(`${BASE_URL}/local-leaderboard/my-rank`, { headers }),
        ]);

        const profileData = profileRes.data.data;
        const myRankData = rankRes.data.data.my_rank;

        setEditName(profileData?.name || "");
        setHistory(historyRes.data.data);

        setProfile({
          ...profileData,
          rank: myRankData?.rank || null,
        });
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditImage(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleOpenModal = () => {
    setEditName(profile?.name || "");
    setEditPassword("");
    setEditConfirm("");
    setEditImage(null);
    setEditImagePreview(null);
    setEditError(null);
    setEditSuccess(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditError(null);
    setEditImage(null);
    setEditImagePreview(null);
  };

  const handleEditSubmit = async () => {
    if (editPassword && editPassword !== editConfirm) {
      setEditError(t("common.passwordsNotMatch"));
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const form = new FormData();
      form.append("name", editName);

      if (editPassword) {
        form.append("password", editPassword);
        form.append("password_confirmation", editConfirm);
      }

      if (editImage) {
        form.append("image", editImage);
      }

      const res = await axios.post(`${BASE_URL}/update-profile`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      const updated = res.data.data;

      setProfile((prev) => ({
        ...prev,
        name: updated.name,
        image: updated.image,
      }));

      dispatch(
        updateUserProfile({
          name: updated.name,
          image: updated.image,
        }),
      );

      setEditSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setEditSuccess(false);
        setEditPassword("");
        setEditConfirm("");
        setEditImage(null);
        setEditImagePreview(null);
      }, 1500);
    } catch (err) {
      setEditError(err.response?.data?.message || t("common.updateFailed"));
    } finally {
      setEditLoading(false);
    }
  };

  const allMatches = history.flatMap((round) => round.matches);
  const totalPoints = history.reduce(
    (sum, r) => sum + r.summary.total_points,
    0,
  );
  const totalPredictions = history.reduce(
    (sum, r) => sum + r.summary.predictions_count,
    0,
  );
  const totalCorrect = history.reduce(
    (sum, r) => sum + r.summary.correct_predictions_count,
    0,
  );
  const overallAccuracy =
    totalPredictions > 0
      ? Math.round((totalCorrect / totalPredictions) * 100)
      : 0;

  if (loading)
    return <div className={styles.loader}>{t("common.loading")}</div>;
  const getImage = (img) => {
    if (!img || img.trim() === "" || img === "null") return "/Margin.png";
    return img;
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.profileBox}>
          <img
            src={getImage(profile?.image)}
            alt="Profile"
            className={styles.profileImg}
          />{" "}
          <div>
            <h1>{profile?.name || "—"}</h1>
            <p>
              @
              {profile?.username ||
                profile?.name?.toLowerCase().replace(" ", "_") ||
                "—"}
            </p>
          </div>
        </div>
        <button className={styles.editBtn} onClick={handleOpenModal}>
          {t("common.editProfile")}
        </button>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.card}>
          <span>{t("common.rank")}</span>
          <h2>{profile?.rank ? `#${profile.rank}` : "—"}</h2>
        </div>
        <div className={styles.card}>
          <span>{t("common.totalPoints")}</span>
          <h2>{totalPoints}</h2>
        </div>
        <div className={styles.card}>
          <span>{t("common.accuracy")}</span>
          <h2>{overallAccuracy}%</h2>
        </div>
        <div className={styles.card}>
          <span>{t("common.predictions")}</span>
          <h2>{totalPredictions}</h2>
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.title}>{t("common.recentPredictions")}</h2>
        <p className={styles.subTitle}>{t("common.latestMatches")}</p>
        <div className={styles.activityList}>
          <div className={styles.headerRow}>
            <div></div>
            <div className={styles.rightHeader}>
              <span>{t("common.yourPrediction")}</span>
              <span>{t("common.result")}</span>
              <span>{t("common.points")}</span>
            </div>
          </div>

          {allMatches.length === 0 ? (
            <p style={{ textAlign: "center", opacity: 0.5, padding: "2rem" }}>
              {t("common.noPredictions")}
            </p>
          ) : (
            allMatches.map((item) => {
              const isWin = item.prediction.is_correct_result;
              const matchLabel = `${item.fixture.home_team.name} vs ${item.fixture.away_team.name}`;
              const predLabel = `${item.prediction.home_score}-${item.prediction.away_score}`;
              const resultLabel =
                item.fixture.status === "finished"
                  ? `${item.fixture.home_team.score}-${item.fixture.away_team.score}`
                  : "—";

              return (
                <div key={item.prediction_id} className={styles.activityCard}>
                  <div className={styles.left}>
                    <div
                      className={isWin ? styles.iconSuccess : styles.iconFail}
                    >
                      {isWin ? "✓" : "✕"}
                    </div>
                    <div>
                      <p className={styles.match}>{matchLabel}</p>
                      <span className={styles.date}>
                        {formatDate(item.fixture.match_date)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.right}>
                    <div className={styles.mobileStat}>
                      <small>{t("common.yourPrediction")}</small>
                      <strong>{predLabel}</strong>
                    </div>

                    <div className={styles.mobileStat}>
                      <small>{t("common.result")}</small>
                      <strong>{resultLabel}</strong>
                    </div>

                    <div className={styles.mobileStat}>
                      <small>{t("common.points")}</small>
                      <span
                        className={isWin ? styles.pointsWin : styles.pointsLose}
                      >
                        {item.prediction.points > 0
                          ? `+${item.prediction.points}`
                          : "0"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>{t("profileModal.title")}</h2>

            <div
              className={styles.avatarEdit}
              onClick={() => fileInputRef.current.click()}
            >
              <img
                src={editImagePreview || getImage(profile?.image)}
                alt="avatar"
                className={styles.avatarPreview}
              />{" "}
              <div className={styles.avatarOverlay}>
                {t("profileModal.changePhoto")}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}> {t("profileModal.name")}</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className={styles.input}
                placeholder={t("profileModal.namePlaceholder")}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("profileModal.newPassword")}
              </label>{" "}
              <input
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                className={styles.input}
                placeholder={t("profileModal.newPasswordPlaceholder")}
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.label}>
                {t("profileModal.confirmPassword")}
              </label>

              <input
                type="password"
                value={editConfirm}
                onChange={(e) => setEditConfirm(e.target.value)}
                className={styles.input}
                placeholder={t("profileModal.confirmPasswordPlaceholder")}
              />
            </div>

            {editError && (
              <p style={{ color: "red", textAlign: "center", marginTop: 8 }}>
                {editError}
              </p>
            )}
            {editSuccess && (
              <p style={{ color: "green", textAlign: "center", marginTop: 8 }}>
                {t("profileModal.success")}
              </p>
            )}

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={handleCloseModal}>
                {t("profileModal.cancel")}
              </button>
              <button
                className={styles.submitBtn}
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading
                  ? t("profileModal.saving")
                  : t("profileModal.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
